import './style/statsActions.scss';

import {capitalize, Loader, parseMoney} from '@dvrd/dvr-controls';

import React, {MouseEventHandler, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import Subscription from '../../../../models/subscriptionModel';

import {DiscountType, SubscriptionStatus} from '../../../../util/interfaces';

import classNames from 'classnames';

interface Props {
    onOpenCustomerDetails: MouseEventHandler;
    onClickPortal: MouseEventHandler;
    onClickCoupon: MouseEventHandler;
    onClickFailedPayment: MouseEventHandler;
    onClickStart: MouseEventHandler;
    onCancelSubscription: MouseEventHandler;
    orderCount: number;
    loading: boolean;
    subscription: Subscription;
}

type SubscriptionAction = {label: string; onClick: MouseEventHandler};
const languageKey = 'settings.subscription';

export default function StatsActions(props: Props) {
    const {t} = useTranslation();
    const {
        orderCount,
        loading,
        subscription,
        onOpenCustomerDetails,
        onClickPortal,
        onClickCoupon,
        onClickFailedPayment,
        onClickStart,
        onCancelSubscription
    } = props;

    const actions = useMemo(() => {
        const {status, failedPayment, paymentMethods} = subscription;
        if (status === SubscriptionStatus.FREE) return [];
        const actions: Array<SubscriptionAction> = [
            {label: t(`${languageKey}.editDetails`, 'Gegevens wijzigen'), onClick: onOpenCustomerDetails},
            {label: t(`${languageKey}.viewInvoices`, 'Facturen bekijken'), onClick: onClickPortal},
            {label: t(`${languageKey}.applyCoupon`, 'Coupon toepassen'), onClick: onClickCoupon}
        ];
        if (failedPayment?.paymentUrl.length)
            actions.splice(0, 0, {
                label: t(`${languageKey}.payOpenInvoice`, 'Openstaande factuur betalen'),
                onClick: onClickFailedPayment
            });
        else if (failedPayment)
            actions.splice(0, 0, {
                label: t(`${languageKey}.payOpenInvoice`, 'Openstaande factuur betalen'),
                onClick: onClickFailedPayment
            });
        else if (status === SubscriptionStatus.INACTIVE || !paymentMethods.length)
            return actions.concat([
                {
                    label: t(`${languageKey}.startSubscriptionAction`, 'Abonnement starten'),
                    onClick: onClickStart
                }
            ]);
        else {
            const startStopAction = status === SubscriptionStatus.CANCELED ? onClickStart : onCancelSubscription;
            const startStopLabel =
                status === SubscriptionStatus.CANCELED
                    ? t(`${languageKey}.start`, 'starten')
                    : t(`${languageKey}.stop`, 'stopzetten');
            actions.push(
                {
                    label: t(`${languageKey}.subscriptionAction`, 'Abonnement {{action}}', {action: startStopLabel}),
                    onClick: startStopAction
                },
                {label: t(`${languageKey}.changePaymentMethod`, 'Betaalwijze aanpassen'), onClick: onClickStart}
            );
        }
        return actions;
    }, [subscription.status, subscription.paymentMethods, subscription.failedPayment, t]);

    function renderSmallLoader() {
        return <Loader active={loading} size='2.5rem' thickness='.1rem' />;
    }

    function renderOrderCount() {
        return (
            <div className='block invoice-count'>
                {renderSmallLoader()}
                <label className='block-title'>{t(`${languageKey}.numOrders`, 'Aantal orders')}</label>
                <label className='block-sub'>{t(`${languageKey}.last30Days`, 'afgelopen 30 dagen')}</label>
                <label className='count'>{orderCount}</label>
            </div>
        );
    }

    function renderStatus() {
        const {status, isTrialing} = subscription;
        let statusClass: string;
        if (isTrialing && ![SubscriptionStatus.ACTIVE, SubscriptionStatus.CANCELED].includes(status))
            statusClass = 'trialing';
        else statusClass = status;

        return (
            <div className='block status'>
                {renderSmallLoader()}
                <label className='block-title'>{t(`${languageKey}.statusTitle`, 'Status')}</label>
                <label className='block-sub'>{t(`${languageKey}.currentSubscriptionSub`, 'huidig abonnement')}</label>
                <div className='row'>
                    <label className={classNames('sub-status', statusClass)}>
                        {t(`${languageKey}.status${capitalize(subscription.actualStatus)}`, subscription.statusLabel)}
                    </label>
                    {renderDiscount()}
                </div>
                {renderPaymentMethod()}
                {isTrialing && subscription.trialEndDate && (
                    <label className='trial-end-date'>{renderTrialText()}</label>
                )}
                {renderDiscountDuration()}
            </div>
        );
    }

    function renderDiscount() {
        if (!subscription.hasActiveDiscount()) return null;
        const {discount, discountType} = subscription;
        if (discountType === DiscountType.PERCENT)
            return (
                <label className='sub-status blue'>
                    {t(`${languageKey}.discountPercent`, '{{discount}}% korting', {discount})}
                </label>
            );
        return (
            <label className='sub-status blue sm'>
                {t(`${languageKey}.discountAmount`, '€{{amount}} korting', {amount: parseMoney(discount)})}
            </label>
        );
    }

    function renderPaymentMethod() {
        if (!subscription.hasPaymentMethod) return null;
        const paymentMethod = subscription.paymentMethods[0];
        return (
            <div className='payment-details'>
                <label>{t(`${languageKey}.paymentMethodLabel`, 'Betaalmethode:')}&nbsp;</label>
                <label>{paymentMethod.displayPaymentType}</label>
                <label>{t(`${languageKey}.accountNumberLabel`, 'Rekeningnummer:')}&nbsp;</label>
                <label>
                    {paymentMethod.bank}
                    <span className='stars'>****</span>
                    {paymentMethod.accountNumber}
                </label>
            </div>
        );
    }

    function renderTrialText() {
        const {status, trialEndDate} = subscription;
        if (status === SubscriptionStatus.ACTIVE)
            return t(`${languageKey}.trialUntilThenPaid`, 'Proef nog tot {{date}}, daarna betaald', {
                date: trialEndDate!.format('DD-MM-YYYY')
            });
        return t(`${languageKey}.until`, 'tot {{date}}', {date: trialEndDate!.format('DD-MM-YYYY')});
    }

    function renderDiscountDuration() {
        const {discountUntil} = subscription;
        if (!discountUntil || !subscription.hasActiveDiscount()) return null;
        return (
            <label className='trial-end-date'>
                {t(`${languageKey}.discountValidUntil`, 'Korting geldig tot {{date}}', {
                    date: discountUntil.format('DD-MM-YYYY')
                })}
            </label>
        );
    }

    function renderActionsContainer() {
        return (
            <div className='block actions'>
                {renderSmallLoader()}
                <label className='block-title'>{t(`${languageKey}.currentSubscription`, 'Huidig abonnement')}</label>
                {renderActions()}
            </div>
        );
    }

    function renderActions() {
        if (!actions.length) return null;
        return actions.map((action, index) => (
            <label key={index} className='subscription-action' onClick={action.onClick}>
                {action.label}
            </label>
        ));
    }

    return (
        <div className='subscription-stats-actions'>
            {renderOrderCount()}
            {renderStatus()}
            {renderActionsContainer()}
        </div>
    );
}

StatsActions.displayName = 'StatsActions';
